import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useProjectStore } from '../../store/projectStore';
import { useStockMovementStore } from '../../store/stockMovementStore';
import { useStockUiStore, StockMovementSubTab } from '../../store/stockUiStore';
import AddStockForm from './movement/AddStockForm';
import ReturnStockForm from './movement/ReturnStockForm';
import TransferForm from './movement/TransferForm';
import CheckOutForm from './movement/CheckOutForm';
import ReviewCommitPanel from './movement/ReviewCommitPanel';

type SubTab = StockMovementSubTab;

const SUB_TABS: { key: SubTab; label: string; description: string; detail: string }[] = [
  {
    key: 'add_stock',
    label: 'Add Stock',
    description: 'Record new items arriving into inventory.',
    detail: 'Use this when stock is physically received — e.g. a delivery, purchase, or donation. Choose the item type, quantity, condition, and destination warehouse.',
  },
  {
    key: 'return_stock',
    label: 'Return Stock',
    description: 'Return checked-out units back into inventory.',
    detail: 'Use this when someone returns items that were checked out. Select the units being returned and the warehouse they should go back into.',
  },
  {
    key: 'transfer',
    label: 'Transfer',
    description: 'Move units between warehouses or storage areas.',
    detail: 'Use this to relocate in-stock units from one warehouse or storage area to another. No ownership changes — purely a location move.',
  },
  {
    key: 'check_out',
    label: 'Check Out',
    description: 'Assign units to a person or team.',
    detail: 'Use this when handing stock to an owner. Select the units leaving inventory, who they\'re going to, and optionally a reference such as a job or project number.',
  },
];

// ── Tooltip ─────────────────────────────────────────────────────────────────
const TabTooltip: React.FC<{
  description: string;
  isDark: boolean;
  anchorRef: React.RefObject<HTMLButtonElement>;
  visible: boolean;
}> = ({ description, isDark, anchorRef, visible }) => {
  const [pos, setPos] = useState({ left: 0, top: 0 });
  const tipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!visible || !anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setPos({ left: rect.left, top: rect.bottom + 8 });
  }, [visible, anchorRef]);

  if (!visible) return null;

  return (
    <div
      ref={tipRef}
      className="fixed z-[200] pointer-events-none rounded-md px-2.5 py-1.5 text-[11px] whitespace-nowrap"
      style={{
        left: pos.left,
        top: pos.top,
        backgroundColor: isDark ? '#1e2330' : '#1f2937',
        color: '#e5e7eb',
        border: `1px solid ${isDark ? '#3a4155' : '#374151'}`,
        boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
      }}
    >
      {description}
    </div>
  );
};

const TOAST_SHOW_MS = 2000;
const TOAST_FADE_MS = 400;

const StockMovementView: React.FC = () => {
  const { appTheme } = useProjectStore();
  const isDark = appTheme === 'dark';
  const { pendingActions, lastResult, clearLastResult } = useStockMovementStore();
  const { activeMovementSubTab, setActiveMovementSubTab } = useStockUiStore();

  const activeSubTab = activeMovementSubTab;
  const setActiveSubTab = setActiveMovementSubTab;
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [hoveredTab, setHoveredTab] = useState<SubTab | null>(null);
  const [toastVisible, setToastVisible] = useState(false);
  const [isToastHovered, setIsToastHovered] = useState(false);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tabRefs = useRef<Record<SubTab, React.RefObject<HTMLButtonElement>>>({
    add_stock:    React.createRef<HTMLButtonElement>(),
    return_stock: React.createRef<HTMLButtonElement>(),
    transfer:     React.createRef<HTMLButtonElement>(),
    check_out:    React.createRef<HTMLButtonElement>(),
  });

  // Listen for "open review" event dispatched from header button
  useEffect(() => {
    const handler = () => setIsReviewOpen(true);
    window.addEventListener('stock-movement:open-review', handler);
    return () => window.removeEventListener('stock-movement:open-review', handler);
  }, []);

  const clearFadeTimer = useCallback(() => {
    if (fadeTimerRef.current) {
      clearTimeout(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }
  }, []);

  const scheduleToastFade = useCallback(() => {
    clearFadeTimer();
    fadeTimerRef.current = setTimeout(() => setToastVisible(false), TOAST_SHOW_MS);
  }, [clearFadeTimer]);

  useEffect(() => {
    if (!lastResult) {
      setToastVisible(false);
      setIsToastHovered(false);
      clearFadeTimer();
      return;
    }
    setToastVisible(true);
    scheduleToastFade();
    return clearFadeTimer;
  }, [lastResult, scheduleToastFade, clearFadeTimer]);

  useEffect(() => {
    if (!lastResult) return;
    if (isToastHovered) {
      clearFadeTimer();
      setToastVisible(true);
    } else {
      scheduleToastFade();
    }
  }, [isToastHovered, lastResult, clearFadeTimer, scheduleToastFade]);

  const handleToastTransitionEnd = () => {
    if (!toastVisible && lastResult) clearLastResult();
  };

  const dismissToast = () => {
    clearFadeTimer();
    setToastVisible(false);
    clearLastResult();
  };

  const pageBg  = isDark ? '#171a1f' : '#f3f4f6';
  const barBg   = isDark ? '#20242b' : '#f0f1f3';
  const border  = isDark ? '#343a43' : '#e5e7eb';
  const muted   = isDark ? '#9ca3af' : '#6b7280';
  const activeTabBg  = isDark ? '#171a1f' : '#ffffff';
  const activeTabTxt = isDark ? '#ffffff' : '#111827';
  const inactiveTabBg  = isDark ? '#2b3140' : '#e5e7eb';
  const inactiveTabTxt = isDark ? '#e5e7eb' : '#374151';

  return (
    <div className="flex-1 flex flex-col overflow-hidden relative" style={{ backgroundColor: pageBg }}>

      {/* Sub-tab bar */}
      <div
        className="flex items-center gap-1 px-4 pt-2 pb-0 flex-shrink-0"
        style={{ backgroundColor: barBg, borderBottom: `1px solid ${border}` }}
      >
        {SUB_TABS.map(tab => {
          const isActive = activeSubTab === tab.key;
          const isHovered = hoveredTab === tab.key;
          const pendingForTab = pendingActions.filter(a => a.type === tab.key).length;
          return (
            <button
              key={tab.key}
              ref={tabRefs.current[tab.key]}
              onClick={() => setActiveSubTab(tab.key)}
              onMouseEnter={() => setHoveredTab(tab.key)}
              onMouseLeave={() => setHoveredTab(null)}
              className="relative inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-t-md transition-colors"
              style={{
                backgroundColor: isActive ? activeTabBg : inactiveTabBg,
                color: isActive ? activeTabTxt : inactiveTabTxt,
                marginBottom: '-1px',
                border: '1px solid transparent',
                borderTopColor: isActive ? border : 'transparent',
                borderLeftColor: isActive ? border : 'transparent',
                borderRightColor: isActive ? border : 'transparent',
                borderBottomColor: isActive ? activeTabBg : 'transparent',
              }}
            >
              {tab.label}
              {pendingForTab > 0 && (
                <span
                  className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold leading-none"
                  style={{ backgroundColor: '#3b82f6', color: '#fff' }}
                >
                  {pendingForTab}
                </span>
              )}
              <TabTooltip
                description={tab.description}
                isDark={isDark}
                anchorRef={tabRefs.current[tab.key]}
                visible={isHovered && !isActive}
              />
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {activeSubTab === 'add_stock'    && <AddStockForm    onQueued={() => {}} onOpenReview={() => setIsReviewOpen(true)} />}
        {activeSubTab === 'return_stock' && <ReturnStockForm onQueued={() => {}} onOpenReview={() => setIsReviewOpen(true)} />}
        {activeSubTab === 'transfer'     && <TransferForm    onQueued={() => {}} onOpenReview={() => setIsReviewOpen(true)} />}
        {activeSubTab === 'check_out'    && <CheckOutForm    onQueued={() => {}} onOpenReview={() => setIsReviewOpen(true)} />}
      </div>

      {/* Review panel (slide-in overlay) */}
      {isReviewOpen && (
        <ReviewCommitPanel
          onClose={() => setIsReviewOpen(false)}
          onCommitted={() => { setIsReviewOpen(false); }}
        />
      )}

      {/* Post-commit result toast */}
      {lastResult && (
        <div
          onMouseEnter={() => setIsToastHovered(true)}
          onMouseLeave={() => setIsToastHovered(false)}
          onTransitionEnd={handleToastTransitionEnd}
          className="absolute bottom-4 right-4 z-50 max-w-sm rounded-lg px-4 py-3 shadow-xl text-sm transition-opacity"
          style={{
            opacity: toastVisible ? 1 : 0,
            transitionDuration: `${TOAST_FADE_MS}ms`,
            pointerEvents: toastVisible ? 'auto' : 'none',
            backgroundColor: lastResult.success ? (isDark ? '#14532d' : '#dcfce7') : (isDark ? '#7f1d1d' : '#fee2e2'),
            border: `1px solid ${lastResult.success ? '#16a34a' : '#dc2626'}`,
            color: lastResult.success ? (isDark ? '#4ade80' : '#15803d') : (isDark ? '#fca5a5' : '#991b1b'),
          }}
        >
          <div className="font-medium mb-0.5">{lastResult.success ? 'Committed successfully' : 'Commit had errors'}</div>
          <div style={{ color: muted }}>{lastResult.message}</div>
          {lastResult.success && (
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5" style={{ color: muted }}>
              {(lastResult.counts.added > 0) && <span>+{lastResult.counts.added} added</span>}
              {(lastResult.counts['returned'] > 0) && <span>+{lastResult.counts['returned']} returned</span>}
              {(lastResult.counts.transferred > 0) && <span>{lastResult.counts.transferred} transferred</span>}
              {(lastResult.counts.checkedOut > 0) && <span>{lastResult.counts.checkedOut} checked out</span>}
            </div>
          )}
          <button
            onClick={dismissToast}
            className="mt-2 text-xs underline opacity-70 hover:opacity-100"
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
};

export default StockMovementView;
